import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import GameHeader from './components/GameHeader';
import GameArea3D from './components/GameArea3D';
import GameControls from './components/GameControls';
import GameOverModal from './components/GameOverModal';
import Instructions from './components/Instructions';
import SignInPage from './components/SignInPage';
import GameSessionManager from './components/GameSessionManager';
import GroupManager from './components/GroupManager';

function App() {
  // Game constants
  const CAR_START_POSITION = 0; // 3D position (z-axis)
  const CLIENT_POSITION = 1000; // 3D finish line position
  const TAP_PUSH_AMOUNT = 10; // Movement per tap in 3D space
  const MAX_SPEED = 10; // Maximum TPS
  const SPEED_DECAY = 0.95;
  
  const [gameState, setGameState] = useState('idle'); // idle, playing, won
  const [tapCount, setTapCount] = useState(0);
  const [carPosition, setCarPosition] = useState(CAR_START_POSITION); // 3D z position
  const [speed, setSpeed] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(0);
  const [gameEndTime, setGameEndTime] = useState(0);
  const [showSignIn, setShowSignIn] = useState(true);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [showGroupManager, setShowGroupManager] = useState(false);
  
  const lastTapTime = useRef(0);
  const animationId = useRef(null);
  const gameSessionRef = useRef(null);
  const multiplayerCarsRef = useRef(null);

  // Calculate progress percentage
  const getProgress = useCallback(() => {
    const totalDistance = CLIENT_POSITION - CAR_START_POSITION;
    const currentDistance = carPosition - CAR_START_POSITION;
    const progress = Math.max(0, Math.min(100, (currentDistance / totalDistance) * 100));
    return progress;
  }, [carPosition]);

  // Reset game state
  const resetGame = useCallback(() => {
    setGameState('idle');
    setTapCount(0);
    setCarPosition(CAR_START_POSITION);
    setSpeed(0);
    setGameStartTime(0);
    setGameEndTime(0);
    lastTapTime.current = 0;
    
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
      animationId.current = null;
    }
  }, []);

  // Handle sign in
  const handleSignIn = useCallback((device) => {
    console.log('App: Sign in with device and group info:', device);
    setDeviceInfo(device);
    
    // If device has group info, set current group
    if (device.groupId && device.playerInfo) {
      setCurrentGroup({
        groupId: device.groupId,
        playerInfo: device.playerInfo,
        status: 'waiting'
      });
    }
    
    setShowSignIn(false);
  }, []);

  // Show sign in page
  const showSignInPage = useCallback(() => {
    setShowSignIn(true);
    setDeviceInfo(null);
    resetGame();
  }, [resetGame]);

  // Handle game completion
  const handleGameComplete = useCallback((gameResult) => {
    console.log('Game completed:', gameResult);
    // Game completion is handled by the winGame function
  }, []);

  // Handle group updates
  const handleGroupUpdate = useCallback((groupInfo) => {
    if (groupInfo && groupInfo.status === 'active') {
      // Game started by admin
      startGame();
    }
    setCurrentGroup(groupInfo);
  }, []);

  // Handle group joined
  const handleGroupJoined = useCallback((groupData) => {
    setCurrentGroup(groupData);
    if (groupData) {
      setShowGroupManager(false);
    }
  }, []);

  // Show group manager
  const showGroupManagerPage = useCallback(() => {
    setShowGroupManager(true);
    setGameState('idle');
  }, []);

  // Win game
  const winGame = useCallback((endTime) => {
    console.log('winGame', endTime)
    setGameState('won');
    setGameEndTime(endTime);
    console.log('gameSessionRef current ', gameSessionRef.current)
    // Notify game session manager
    if (gameSessionRef.current) {
      gameSessionRef.current.handleGameComplete(true, tapCount, (endTime - gameStartTime) / 1000);
    }
    
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
      animationId.current = null;
    }
  }, [tapCount, gameStartTime]);

  // Handle tap events
  const handleTap = useCallback((event) => {
    if (gameState !== 'playing' && gameState !== 'paused') return;
    
    event.preventDefault();
    
    const currentTime = Date.now();
    setTapCount(prev => prev + 1);
    
    // Calculate speed based on taps per second
    if (lastTapTime.current > 0) {
      const tapInterval = currentTime - lastTapTime.current;
      // Convert to TPS: (1000ms / interval) = taps per second
      const newSpeed = Math.min(MAX_SPEED, 1000 / tapInterval);
      setSpeed(newSpeed);
    }
    lastTapTime.current = currentTime;
    
    // Move car forward in 3D space
    setCarPosition(prev => {
      const newPosition = prev + TAP_PUSH_AMOUNT;
      if (newPosition >= CLIENT_POSITION) {
        winGame(currentTime);
        return CLIENT_POSITION;
      }
      return newPosition;
    });

    // Update all other players' positions when we tap
    if (multiplayerCarsRef.current && currentGroup?.groupId) {
      console.log('App: Tap detected, calling updateAllPlayersPositions for group:', currentGroup.groupId);
      multiplayerCarsRef.current.updateAllPlayersPositions();
    } else {
      console.log('App: Cannot update players - multiplayerCarsRef:', !!multiplayerCarsRef.current, 'groupId:', currentGroup?.groupId);
    }
  }, [gameState, winGame, currentGroup?.groupId]);

  // Update progress when car position changes
  useEffect(() => {
    if (gameState === 'playing' && gameSessionRef.current && currentGroup?.groupId) {
      const progress = getProgress();
      console.log('App: Progress updated to:', progress, '% for group:', currentGroup.groupId);
      gameSessionRef.current.updateProgress(progress);
    }
  }, [carPosition, gameState, currentGroup?.groupId, getProgress]);

  // Start speed decay animation
  const startSpeedDecay = useCallback(() => {
    const decaySpeed = () => {
      if (gameState === 'playing') {
        setSpeed(prev => {
          const newSpeed = prev * SPEED_DECAY;
          return newSpeed < 1 ? 0 : newSpeed;
        });
        animationId.current = requestAnimationFrame(decaySpeed);
      }
    };
    decaySpeed();
  }, [gameState]);

  // Start game
  const startGame = useCallback(() => {
    if (gameState === 'playing') return;
    
    setGameState('playing');
    setGameStartTime(Date.now());
    startSpeedDecay();
  }, [gameState, startSpeedDecay]);

  // Resume game from paused state
  const resumeGame = useCallback(() => {
    if (gameState !== 'paused') return;
    
    setGameState('playing');
    startSpeedDecay();
  }, [gameState, startSpeedDecay]);

  // Handle keyboard input
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      if (gameState === 'playing' || gameState === 'paused') {
        handleTap(event);
      }
    } else if (event.key === ' ' || event.key === 'Spacebar') {
      // Space bar to start/resume game
      event.preventDefault();
      if (gameState === 'idle') {
        startGame();
      } else if (gameState === 'paused') {
        resumeGame();
      }
    } else if (event.key === 'Enter') {
      // Enter key to start/resume game
      if (gameState === 'idle') {
        startGame();
      } else if (gameState === 'paused') {
        resumeGame();
      }
    } else if (event.key === 'Escape') {
      // Escape key to reset game
      resetGame();
    }
  }, [gameState, handleTap, startGame, resumeGame, resetGame]);

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Calculate final stats
  const getFinalStats = useCallback(() => {
    if (!gameStartTime || !gameEndTime) return { time: 0, taps: 0, avgSpeed: 0 };
    
    const gameTime = (gameEndTime - gameStartTime) / 1000;
    return {
      time: gameTime.toFixed(1),
      taps: tapCount,
      avgSpeed: Math.round(speed)
    };
  }, [gameStartTime, gameEndTime, tapCount, speed]);

  // Reset game on mount (page refresh)
  useEffect(() => {
    // Ensure game starts in proper initial state
    setGameState('idle');
    setTapCount(0);
    setCarPosition(CAR_START_POSITION);
    setSpeed(0);
    setGameStartTime(0);
    setGameEndTime(0);
    lastTapTime.current = 0;
  }, []); // Empty dependency array means this runs once on mount

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
      }
    };
  }, []);

  // Handle visibility change to pause game when tab is not active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && gameState === 'playing') {
        setGameState('paused');
        if (animationId.current) {
          cancelAnimationFrame(animationId.current);
          animationId.current = null;
        }
      } else if (!document.hidden && gameState === 'paused') {
        // Resume game when tab becomes visible again
        setGameState('playing');
        startSpeedDecay();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [gameState, startSpeedDecay]);

  return (
    <div className="App">
      {showSignIn ? (
        <SignInPage 
          onSignIn={handleSignIn}
          onShowGame={() => setShowSignIn(false)}
        />
      ) : (
        <div className="game-container" data-paused={gameState === 'paused'}>
          {deviceInfo && (
            <GameSessionManager
              ref={gameSessionRef}
              deviceInfo={deviceInfo}
              onGameComplete={handleGameComplete}
              onGroupUpdate={handleGroupUpdate}
            />
          )}
          
          <GameHeader 
            tapCount={tapCount} 
            speed={speed}
            deviceInfo={deviceInfo}
            onSignOut={showSignInPage}
          />
          
          <GameArea3D
            ref={multiplayerCarsRef}
            carPosition={carPosition}
            gameState={gameState}
            onTap={handleTap}
            progress={getProgress()}
            playerLane={currentGroup?.playerInfo?.laneNumber || 0}
            playerColor={currentGroup?.playerInfo?.color || '#FF6B6B'}
            groupId={currentGroup?.groupId || null}
            deviceInfo={deviceInfo}
          />
          
          <GameControls
            gameState={gameState}
            onStart={startGame}
            onResume={resumeGame}
            onReset={resetGame}
            onShowGroups={showGroupManagerPage}
            currentGroup={currentGroup}
          />
          
          {gameState === 'won' && (
            <GameOverModal
              stats={getFinalStats()}
              onPlayAgain={resetGame}
              deviceInfo={deviceInfo}
            />
          )}
          
          <Instructions />
          
          {showGroupManager && (
            <GroupManager
              deviceInfo={deviceInfo}
              onGroupJoined={handleGroupJoined}
              onGameStart={handleGroupUpdate}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default App;
